import React, {useEffect, useState} from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {CatchStatsByWaterChart} from '../components/charts/CatchStatsByWaterChart';
import {FishStatsChart} from '../components/charts/FishStatsChart';
import {CatchOverviewDonut} from '../components/charts/CatchOverviewDonut';
import {ActivityChart} from '../components/charts/ActivityChart';
import {CatchesByTimeChart} from '../components/charts/CatchesByTimeChart';
import {ActivityHeatmap} from '../components/charts/ActivityHeatmap';
import {getData} from '../utils/storage/Storage';
import {StorageKey} from '../utils/storage/Keys';
import axios from 'axios';
import { useSelectedOrganisationContext } from "../custom-hooks/useOrganisation";

interface CatchOverview {
  totalCatches: number;
  totalTaken: number;
  totalReleased: number;
  totalUnknown: number;
  totalMembers: number;
  activeFishermen: number;
}

interface WaterStat {
  waterId: string;
  waterName: string;
  totalCatches: number;
  taken: number;
  released: number;
  unknown: number;
}

interface FishStat {
  fishId: string;
  fishName: string;
  totalCatches: number;
  taken: number;
  released: number;
  avgLength: number | null;
  avgWeight: number | null;
}

interface MemberStats {
  totalMembers: number;
  reportedCatchYears: number;
}

interface CatchByTime {
  year: number;
  month: number;
  totalCatches: number;
  taken: number;
  released: number;
  unknown: number;
}

export default function DashboardHome() {
  const [selectedYear, setSelectedYear] = useState<number>(-1);
  const [loading, setLoading] = useState<boolean>(true);

  const [memberStats, setMemberStats] = useState<MemberStats | null>(null);
  const [catchOverview, setCatchOverview] = useState<CatchOverview | null>(null);
  const [waterStats, setWaterStats] = useState<WaterStat[]>([]);
  const [fishStats, setFishStats] = useState<FishStat[]>([]);
  const [catchesByTime, setCatchesByTime] = useState<CatchByTime[]>([]);

  const { selectedOrganisation } = useSelectedOrganisationContext();

  useEffect(() => {
    if (selectedOrganisation === null) {
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const token = getData(StorageKey.TOKEN) as string;
        const headers = {Authorization: 'Bearer ' + token};

        const yearParam = selectedYear === -1 ? '' : `?year=${selectedYear}`;

        const [memberRes, overviewRes, waterRes, fishRes, timeRes] = await Promise.all([
          axios.get(`/organisation/${selectedOrganisation.id}/count-all-members${yearParam}`, {headers}),
          axios.get(`/charts/${selectedOrganisation.id}/catch-overview${yearParam}`, {headers}),
          axios.get(`/charts/${selectedOrganisation.id}/catch-stats-by-water${yearParam}`, {headers}),
          axios.get(`/charts/${selectedOrganisation.id}/fish-stats${yearParam}`, {headers}),
          axios.get(`/charts/${selectedOrganisation.id}/catches-by-time${yearParam}`, {headers})
        ]);

        setMemberStats(memberRes.data);
        setCatchOverview(overviewRes.data);
        setWaterStats(waterRes.data);
        setFishStats(fishRes.data);
        setCatchesByTime(timeRes.data);
      } catch (e) {
        console.error('Fehler beim Laden der Dashboard-Daten:', e);
      } finally {
        setLoading(false);
      }
    };

    fetchData().then();
  }, [selectedYear, selectedOrganisation]);

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [-1];
    for (let year = currentYear; year >= currentYear - 5; year--) {
      years.push(year);
    }
    return years;
  };

  if (loading) {
    return (
      <div className="page-body">
        <div className="container-xl">
          <div className="row row-cards">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <div className="text-center">Lade Dashboard...</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-body">
      <div className="container-xl">
        <div className="row g-2 align-items-center mb-3">
          <div className="col">
            <h2 className="page-title">Dashboard - {selectedOrganisation?.organisationName}</h2>
          </div>
          <div className="col-auto">
            <select
              className="form-select"
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              <option value={-1}>Aktuelles Jahr</option>
              {generateYearOptions().slice(1).map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Hero Stats */}
        <div className="row row-cards mb-3">
          <div className="col-sm-6 col-lg-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="subheader">Gesamtfänge</div>
                </div>
                <div className="h1 mb-0">{catchOverview?.totalCatches || 0}</div>
              </div>
            </div>
          </div>
          <div className="col-sm-6 col-lg-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="subheader">Entnahmequote</div>
                </div>
                <div className="h1 mb-0">
                  {catchOverview
                    ? ((catchOverview.totalTaken / catchOverview.totalCatches) * 100).toFixed(1)
                    : 0}
                  %
                </div>
              </div>
            </div>
          </div>
          <div className="col-sm-6 col-lg-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="subheader d-flex align-items-center">
                    Aktive Angler
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tooltip-active-fishermen">
                          Mitglieder, die im gewählten Zeitraum mindestens einen Fang erfasst haben.
                        </Tooltip>
                      }
                    >
                      <span
                        className="ms-1 text-muted d-inline-flex align-items-center"
                        role="img"
                        aria-label="Information zu aktive Angler"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="icon icon-tabler icon-tabler-info-circle"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          strokeWidth="2"
                          stroke="currentColor"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M12 9h.01" />
                          <path d="M11 12h1v4h1" />
                          <path d="M12 3a9 9 0 1 0 9 9a9 9 0 0 0 -9 -9" />
                        </svg>
                      </span>
                    </OverlayTrigger>
                  </div>
                </div>
                <div className="h1 mb-0">
                  {catchOverview?.activeFishermen || 0} / {catchOverview?.totalMembers || 0}
                </div>
              </div>
            </div>
          </div>
          <div className="col-sm-6 col-lg-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="subheader d-flex align-items-center">
                    Fangjahr gemeldet
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tooltip-catchyear">
                          Anzahl der Mitglieder, die ihr Fangjahr bereits gemeldet haben.
                        </Tooltip>
                      }
                    >
                      <span
                        className="ms-1 text-muted d-inline-flex align-items-center"
                        role="img"
                        aria-label="Information zu Fangjahr gemeldet"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="icon icon-tabler icon-tabler-info-circle"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          strokeWidth="2"
                          stroke="currentColor"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                          <path d="M12 9h.01" />
                          <path d="M11 12h1v4h1" />
                          <path d="M12 3a9 9 0 1 0 9 9a9 9 0 0 0 -9 -9" />
                        </svg>
                      </span>
                    </OverlayTrigger>
                  </div>
                </div>
                <div className="h1 mb-0">
                  {memberStats?.reportedCatchYears || 0} / {memberStats?.totalMembers || 0}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row 1 */}
        <div className="row row-cards mb-3">
          <div className="col-lg-6">
            <CatchOverviewDonut data={catchOverview} />
          </div>
          <div className="col-lg-6">
            {catchOverview && memberStats && (
              <ActivityChart
                activeFishermen={catchOverview.activeFishermen}
                totalMembers={catchOverview.totalMembers}
                reportedCatchYears={memberStats.reportedCatchYears}
              />
            )}
          </div>
        </div>

        {/* Charts Row 2 */}
        <div className="row row-cards mb-3">
          <div className="col-lg-6">
            <CatchStatsByWaterChart data={waterStats} />
          </div>
          <div className="col-lg-6">
            <FishStatsChart data={fishStats} />
          </div>
        </div>

        {/* Charts Row 3 - Zeitverlauf & Heatmap */}
        <div className="row row-cards mb-3">
          <div className="col-lg-8">
            <CatchesByTimeChart data={catchesByTime} />
          </div>
          <div className="col-lg-4">
            <ActivityHeatmap data={catchesByTime} />
          </div>
        </div>
      </div>
    </div>
  );
}
